/**
 * The api atoms that a component reads.
 *
 * Each export has a read-only type, so `useSetAtom` does not compile on it and a family does
 * not give `remove`. A refresh, an answer or a command goes through its hook in src/hooks/,
 * and only a container calls that hook.
 */
import type { Atom } from 'jotai';

import type { AgentLoadSnapshot } from '@/types/agentLoad';
import type {
    AgentSettingsSet, AgentSettingsSetKey, AgentsSnapshot, RulesSchemaSnapshot,
    TriggerCountSnapshot,
} from '@/types/agents';
import type {
    BookmarkListFilter, BookmarkListSnapshot, BookmarkTagsSnapshot,
} from '@/types/bookmarks';
import type { CaseWrite, PendingCase } from '@/types/cases';
import type { CameraIndex, DeviceWebSession, EditedCameras, FlatDevice } from '@/types/devices';
import type { EnrollmentChangeNotice } from '@/types/enrollments';
import type { EstimateHistoryRequest, EstimateHistorySnapshot } from '@/types/estimate';
import type { RecentActivitySnapshot } from '@/types/events';
import type { MotionBoxRequest, MotionBoxSnapshot } from '@/types/heatmap';
import type { LayoutRow, LayoutWrite } from '@/types/layouts';
import type { ResourceStatus } from '@/types/notifications';
import type { ArchiveCamera, PlayerDevice, RecordedSegment } from '@/types/player';
import type { FleetServer } from '@/types/servers';

import {
    agentLoadAtom as sourceAgentLoadAtom, noAgentLoadAtom as sourceNoAgentLoadAtom,
} from './api/agentLoadAtoms';
import { agentsAtom as sourceAgentsAtom } from './api/agentsAtoms';
import { agentSettingsOfAtom as sourceAgentSettingsOfAtom } from './api/agentSettingsAtoms';
import {
    bookmarkListAtom as sourceBookmarkListAtom,
    bookmarkTagsAtom as sourceBookmarkTagsAtom,
} from './api/bookmarksAtoms';
import { cameraPreviewUrlAtom as sourceCameraPreviewUrlAtom } from './api/cameraPreviewAtoms';
import {
    caseWriteAtom as sourceCaseWriteAtom, pendingCaseAtom as sourcePendingCaseAtom,
} from './api/casesAtoms';
import {
    cameraAtom as sourceCameraAtom,
    cameraIndexAtom as sourceCameraIndexAtom,
    cameraNameAtom as sourceCameraNameAtom,
    cameraStatusAtom as sourceCameraStatusAtom,
    offlineCameraCountAtom as sourceOfflineCameraCountAtom,
    serverCameraStatusesAtom as sourceServerCameraStatusesAtom,
} from './api/devicesAtoms';
import { deviceWebSessionAtom as sourceDeviceWebSessionAtom } from './api/deviceWebSessionAtoms';
import { enrollmentChangeAtom as sourceEnrollmentChangeAtom } from './api/enrollmentsAtoms';
import { estimateHistoryAtom as sourceEstimateHistoryAtom } from './api/estimateHistoryAtoms';
import {
    layoutRowsAtom as sourceLayoutRowsAtom, layoutWriteAtom as sourceLayoutWriteAtom,
} from './api/layoutsAtoms';
import { editedCamerasAtom as sourceEditedCamerasAtom } from './api/liveCamerasAtoms';
import { liveStatusDownAtom as sourceLiveStatusDownAtom } from './api/liveStatusAtoms';
import { motionBoxAtom as sourceMotionBoxAtom } from './api/motionBoxAtoms';
import { recentActivityAtom as sourceRecentActivityAtom } from './api/recentActivityAtoms';
import { rulesSchemaAtom as sourceRulesSchemaAtom } from './api/rulesSchemaAtoms';
import {
    noTriggerCountAtom as sourceNoTriggerCountAtom,
    ruleTriggerCountAtom as sourceRuleTriggerCountAtom,
} from './api/ruleTriggerCountAtoms';
import {
    layoutSegmentsAtom as sourceLayoutSegmentsAtom,
    liveSegmentsAtom as sourceLiveSegmentsAtom,
    segmentsAtom as sourceSegmentsAtom,
} from './api/segmentsAtoms';
import { fleetServerByIdAtom as sourceFleetServerByIdAtom } from './api/serversAtoms';
import { iceServersAtom as sourceIceServersAtom } from './api/turnAtoms';

/** An atom for each key, as an `atomFamily` gives it, without its `remove`. */
type Family<K, V> = (key: K) => Atom<V>;

/** The agent load of an analytics server; it polls while mounted. */
export const agentLoadAtom: (
    serverId: string, intervalMs: number,
) => Atom<AgentLoadSnapshot> = sourceAgentLoadAtom;

/** The organization's agents: their manifests, and the server that runs each one. */
export const agentsAtom: Atom<AgentsSnapshot> = sourceAgentsAtom;

/** One agent's settings on a set of cameras; the same set in any sequence is one atom. */
export const agentSettingsOfAtom: Family<AgentSettingsSetKey, AgentSettingsSet> =
    sourceAgentSettingsOfAtom;

/** The capped list of bookmarks that a filter asks for, newest first. */
export const bookmarkListAtom: Family<BookmarkListFilter, BookmarkListSnapshot> =
    sourceBookmarkListAtom;

/** The organization's tag vocabulary, most used first; every reader shares one request. */
export const bookmarkTagsAtom: Atom<BookmarkTagsSnapshot> = sourceBookmarkTagsAtom;

/** One camera's row. A reload that leaves the row as it was gives the same object. */
export const cameraAtom: Family<string, FlatDevice | undefined> = sourceCameraAtom;

/** The cameras by id and their names, for a lookup in a loop or a callback. */
export const cameraIndexAtom: Atom<CameraIndex> = sourceCameraIndexAtom;

/** The row's name, else the last one the camera was listed under. */
export const cameraNameAtom: Family<string, string | undefined> = sourceCameraNameAtom;

/** The camera's last preview as a `data:` URL, or null before the first. */
export const cameraPreviewUrlAtom: Family<string, string | null> = sourceCameraPreviewUrlAtom;

/** One camera's status; undefined before its first frame and after the stream was down. */
export const cameraStatusAtom: Family<string, ResourceStatus | undefined> =
    sourceCameraStatusAtom;

/**
 * The cameras that the camera settings panel edits, keyed by `getIdsKey`. The array keeps its
 * identity while no camera changes, and a reload does not read as loading.
 */
export const editedCamerasAtom: Family<string, EditedCameras> = sourceEditedCamerasAtom;

/** The session last answered for one camera's web page, or null before the first. */
export const deviceWebSessionAtom: Family<string, DeviceWebSession | null> =
    sourceDeviceWebSessionAtom;

/** The last enrollment write; a list that holds rows applies each one after its mount. */
export const enrollmentChangeAtom: Atom<EnrollmentChangeNotice | null> =
    sourceEnrollmentChangeAtom;

/** The pages of one rule estimate history, read while shown. */
export const estimateHistoryAtom: Family<EstimateHistoryRequest, EstimateHistorySnapshot> =
    sourceEstimateHistoryAtom;

/** The organization's servers by id, for a row that names its server. */
export const fleetServerByIdAtom: Atom<ReadonlyMap<string, FleetServer>> =
    sourceFleetServerByIdAtom;

/** The rows of the Layouts tab: the listed layouts, with the writes of this session over them. */
export const layoutRowsAtom: Atom<LayoutRow[]> = sourceLayoutRowsAtom;

/** The write of one case field, attachment note or new case by its key, or none. */
export const caseWriteAtom: Family<string, CaseWrite | undefined> = sourceCaseWriteAtom;

/** The case started here that the list does not show yet. */
export const pendingCaseAtom: Atom<PendingCase | undefined> = sourcePendingCaseAtom;

/** The write of one layout, or none. It requests no layouts. */
export const layoutWriteAtom: Family<string | null, LayoutWrite | undefined> =
    sourceLayoutWriteAtom;

/**
 * The segment list of each layout camera by `streamKeyOf`, polled; a camera is absent until its
 * first load. The callers memoize `devices`, so the array keys the atom.
 */
export const layoutSegmentsAtom: (
    devices: readonly PlayerDevice[], intervalMs: number,
) => Atom<ReadonlyMap<string, RecordedSegment[]>> = sourceLayoutSegmentsAtom;

/** The ICE servers of a new peer connection: STUN, plus this member's TURN once it loads. */
export const iceServersAtom: Atom<RTCIceServer[]> = sourceIceServersAtom;

/** The camera's recorded segments, null until the first load; it polls while mounted. */
export const liveSegmentsAtom: (
    camera: ArchiveCamera | null, intervalMs: number,
) => Atom<RecordedSegment[] | null> = sourceLiveSegmentsAtom;

/** True while the notifications stream has been down past its grace period. */
export const liveStatusDownAtom: Atom<boolean> = sourceLiveStatusDownAtom;

/** What a reader of an offline analytics server gets: nothing loads. */
export const noAgentLoadAtom: Atom<AgentLoadSnapshot> = sourceNoAgentLoadAtom;

/** What an editor reads without a saved rule, or without a limit in its draft: nothing loads. */
export const noTriggerCountAtom: Atom<TriggerCountSnapshot> = sourceNoTriggerCountAtom;

/** How many listed cameras the stream last said are offline, for the home launcher. */
export const offlineCameraCountAtom: Atom<number> = sourceOfflineCameraCountAtom;

/** How many objects and events the organization saw in the last day, for the home launcher. */
export const recentActivityAtom: Atom<RecentActivitySnapshot> = sourceRecentActivityAtom;

/** How often the rule has triggered toward its limit; it polls while mounted. */
export const ruleTriggerCountAtom: (
    ruleId: string, intervalMs: number,
) => Atom<TriggerCountSnapshot> = sourceRuleTriggerCountAtom;

/** The rule event and action types; the empty schema until they load. */
export const rulesSchemaAtom: Atom<RulesSchemaSnapshot> = sourceRulesSchemaAtom;

/** The camera's recorded segments, null until the first load, with no poll. */
export const segmentsAtom: Family<ArchiveCamera | null, RecordedSegment[] | null> =
    sourceSegmentsAtom;

/** The motion box of a period; with no period, no box and nothing loads. */
export const motionBoxAtom: Family<MotionBoxRequest | null, MotionBoxSnapshot> =
    sourceMotionBoxAtom;

/** The statuses of one server's listed cameras, for a choice among them. */
export const serverCameraStatusesAtom: Family<string, ReadonlyMap<string, ResourceStatus>> =
    sourceServerCameraStatusesAtom;
